/**
 * 品牌配置弹窗
 *
 * 编辑品牌调性风格、禁用词、必带标签等 AI 生成配置。
 *
 * 设计说明：
 * - 禁用词分两层：①「品牌调性禁用词」由运营在此填写；②「小红书平台通用禁用词」
 *   由后端自动叠加（见 backend XHS_GLOBAL_FORBIDDEN_WORDS），运营无需重复填写。
 *   弹窗下方会展示「当前生效（含平台通用）」合并预览。
 * - 扩展配置由原来的 JSON 文本框改为「键值对可增删表单」，免去手写 JSON。
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import type { BrandConfig } from '@chaoyinli/shared'
import type { BrandConfigParams } from '@/api/services'

interface ExtraRow {
  key: string
  value: string
}

interface BrandConfigDialogProps {
  /** 是否打开 */
  open: boolean
  /** 关闭回调 */
  onClose: () => void
  /** 提交回调 */
  onSubmit: (config: BrandConfigParams) => Promise<void>
  /** 现有配置数据 */
  config?: BrandConfig | null
  /** 品牌名称（用于标题显示） */
  brandName?: string
  /** 是否正在提交 */
  submitting?: boolean
}

export function BrandConfigDialog({
  open,
  onClose,
  onSubmit,
  config,
  brandName,
  submitting = false,
}: BrandConfigDialogProps) {
  const [toneStyle, setToneStyle] = useState('')
  const [forbiddenWords, setForbiddenWords] = useState('')
  const [requiredTags, setRequiredTags] = useState('')
  const [imageStyle, setImageStyle] = useState('')
  const [colorTone, setColorTone] = useState('')
  const [categoryKeywords, setCategoryKeywords] = useState('')
  // 扩展配置：以「键值对列表」形式编辑，提交时拼成对象，免去手写 JSON
  const [extraRows, setExtraRows] = useState<ExtraRow[]>([])

  // 配置数据变化时重置表单
  useEffect(() => {
    if (open) {
      setToneStyle(config?.tone_style || '')
      setForbiddenWords(config?.forbidden_words || '')
      setRequiredTags(config?.required_tags || '')
      setImageStyle(config?.image_style || '')
      setColorTone(config?.color_tone || '')
      setCategoryKeywords(config?.category_keywords || '')
      // 扩展配置：已有则拆成键值对行；数组值用顿号连接展示
      const extra = config?.extra_config
      if (extra && typeof extra === 'object' && Object.keys(extra).length > 0) {
        setExtraRows(
          Object.entries(extra).map(([k, v]) => ({
            key: k,
            value: Array.isArray(v) ? (v as unknown[]).join('、') : String(v ?? ''),
          })),
        )
      } else {
        setExtraRows([])
      }
    }
  }, [open, config])

  const updateRow = (idx: number, patch: Partial<ExtraRow>) => {
    setExtraRows((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  }
  const addRow = () => setExtraRows((rows) => [...rows, { key: '', value: '' }])
  const removeRow = (idx: number) =>
    setExtraRows((rows) => rows.filter((_, i) => i !== idx))

  const handleSubmit = async () => {
    // 扩展配置：仅保留有「名称」的行，拼成对象
    const extraConfig: Record<string, unknown> = {}
    for (const row of extraRows) {
      const k = row.key.trim()
      if (k) extraConfig[k] = row.value
    }

    const data: BrandConfigParams = {
      tone_style: toneStyle.trim(),
      forbidden_words: forbiddenWords.trim(),
      required_tags: requiredTags.trim(),
      image_style: imageStyle.trim(),
      color_tone: colorTone.trim(),
      category_keywords: categoryKeywords.trim(),
      extra_config: extraConfig,
    }
    await onSubmit(data)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '18px' }}>
        品牌配置{brandName ? ` - ${brandName}` : ''}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: '#999', mb: 2, mt: 1 }}>
          配置品牌的 AI 生成调性，影响所有员工的文案生成效果
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="调性风格"
            value={toneStyle}
            onChange={(e) => setToneStyle(e.target.value)}
            size="small"
            fullWidth
            placeholder="例如：活泼可爱、专业沉稳、年轻时尚"
            helperText="AI 生成文案的整体语气和风格"
          />
          <TextField
            label="品牌禁用词（调性相关）"
            value={forbiddenWords}
            onChange={(e) => setForbiddenWords(e.target.value)}
            size="small"
            fullWidth
            placeholder="如：预制菜、冷冻虾、料理包"
            helperText="仅填写该品牌调性相关的禁用词；小红书平台通用禁用词由系统自动叠加，无需重复填写"
          />
          {config?.effective_forbidden_words ? (
            <Box sx={{ mt: -1, mb: 1 }}>
              <Typography variant="caption" sx={{ color: '#666' }}>
                当前生效（含平台通用）：
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: '#999', display: 'block', wordBreak: 'break-all', lineHeight: 1.6 }}
              >
                {config.effective_forbidden_words}
              </Typography>
            </Box>
          ) : null}
          <TextField
            label="必带标签"
            value={requiredTags}
            onChange={(e) => setRequiredTags(e.target.value)}
            size="small"
            fullWidth
            placeholder="例如：#品牌名 #新品上市"
            helperText="每条文案必须包含的标签"
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="图片风格"
              value={imageStyle}
              onChange={(e) => setImageStyle(e.target.value)}
              size="small"
              fullWidth
              placeholder="例如：清新明亮、暖色调"
            />
            <TextField
              label="色调偏好"
              value={colorTone}
              onChange={(e) => setColorTone(e.target.value)}
              size="small"
              fullWidth
              placeholder="例如：橙色系、暖色"
            />
          </Box>
          <TextField
            label="品类关键词"
            value={categoryKeywords}
            onChange={(e) => setCategoryKeywords(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
            placeholder="多个关键词用逗号分隔"
            helperText="AI 生成时参考的品类关键词"
          />

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            扩展配置
          </Typography>
          <Typography variant="body2" sx={{ color: '#999', mb: 1 }}>
            按「名称 — 内容」逐条添加，会一并注入 AI Prompt；无需手写 JSON，留空即不使用。
          </Typography>
          {extraRows.map((row, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="名称"
                value={row.key}
                onChange={(e) => updateRow(idx, { key: e.target.value })}
                size="small"
                sx={{ width: '30%' }}
                placeholder="如：招牌菜"
              />
              <TextField
                label="内容"
                value={row.value}
                onChange={(e) => updateRow(idx, { value: e.target.value })}
                size="small"
                fullWidth
                multiline
                rows={2}
                placeholder="如：秘制青花椒龙虾、蒜泥生蚝"
              />
              <Tooltip title="删除该项">
                <IconButton onClick={() => removeRow(idx)} sx={{ mt: 0.5 }} size="small">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={addRow}
            sx={{ alignSelf: 'flex-start', color: '#FF8C00', textTransform: 'none' }}
          >
            添加一项
          </Button>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: '#666' }}>
          取消
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            backgroundColor: '#FF8C00',
            '&:hover': { backgroundColor: '#E67E00' },
          }}
        >
          {submitting ? '保存中...' : '保存配置'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BrandConfigDialog
